import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';

import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalApiId, AnimalSex, AnimalSexLabel } from '@/model/Animal.model';
import type { Animal, AnimalSpecies } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMetaV16 } from '@/support/hot-v16';

import { ApiService } from '@/support/ApiService';
import { web as webRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { featuresSelector } from '@/support/Selectors';
import { modalAction } from '@/utils/modal';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SubHeader from '@/components/UI/SubHeader';
import { successToast, toNumericId } from '@/helpers';

import { createStrainResolver, getMetaColumns } from '../Colony.utils';
import { initAnimalSettingsV16, initialBulkAnimalDataV16 } from '../Colony.utils.v16';
import BulkAnimalsTableV16 from './BulkAnimalsTableV16';
import '../Colony.scss';

interface BulkAnimalRow {
  id: ID;
  catalog: string;
  name: string;
  dob: string | null;
  cage_catalog: string;
  cage_name: string;
  sex?: string;
  species?: string;
  strain?: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

interface AnimalPatchData {
  api_id: AnimalApiId;
  cage_id: number | null;
  name: string;
  dob: string | null;
  sex?: AnimalSex;
  strain_id?: number | null;
  meta?: Record<string, MetadataFieldValueTypes>;
}

interface AnimalSettingsResultV16 {
  settings: SpreadsheetV16Settings;
  metadata?: Array<MetadataField>;
}

const featureFlagsSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

const BulkAnimalEdit: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { unknownSexEnabled, reasonForChangeEnabled } = useSelector(featureFlagsSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const tableRef = useRef<HotTableRef>(null);

  const [data, setData] = useState<Array<BulkAnimalRow>>([]);
  const [settings, setSettings] = useState<SpreadsheetV16Settings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);
  const [loading, setLoading] = useState(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const { id: studyId } = useParams();

  const selectedAnimals: Array<Animal> = useMemo(
    () => (location.state as { animals?: Animal[] })?.animals ?? [],
    [location.state]
  );

  useEffect(() => {
    if (_isEmpty(selectedAnimals)) {
      navigate(_notNil(studyId) ? webRoute('studies.animals', { id: studyId }) : '/colony', {
        replace: true,
        state: { tab: 1 },
      });
    }
  }, [selectedAnimals, navigate, studyId]);

  const { response: animalMetadataResponse, loading: animalMetadataLoading } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    invokeOnInit: true,
    query: { filter_type: MetadataTypes.subject_meta },
    options: {
      onError: {
        throw: false,
        capture: true,
        toast: false,
        slug: 'bulk-animal-edit-fetch-metadata',
      },
    },
  });

  const { response: speciesResponse, loading: speciesLoading } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'bulk-animal-edit-fetch-species' } },
  });

  const metaColumns = useMemo(() => {
    const metadataResponseData = (animalMetadataResponse?.body ?? []) as Array<MetadataField>;
    if (_isNotEmpty(metadataResponseData) && _isNotEmpty(selectedAnimals)) {
      return getMetaColumns(selectedAnimals, metadataResponseData);
    }
    return [];
  }, [animalMetadataResponse, selectedAnimals]);

  const initialLoad =
    _isNil(animalMetadataResponse?.body) || _isNil(speciesResponse?.body) || animalMetadataLoading || speciesLoading;
  const species = useMemo<Array<AnimalSpecies>>(
    () => (speciesResponse?.body?.data ?? []) as unknown as Array<AnimalSpecies>,
    [speciesResponse]
  );
  const availableMetadata = useMemo<Array<MetadataField>>(
    () => (animalMetadataResponse?.body ?? []) as Array<MetadataField>,
    [animalMetadataResponse]
  );

  useEffect(() => {
    if (!initialLoad && _notNil(metaColumns) && _isNil(settings) && _isNotEmpty(selectedAnimals)) {
      const removeRow = false;

      const newSettings = initAnimalSettingsV16(
        species,
        true,
        removeRow,
        metaColumns,
        availableMetadata,
        undefined,
        entityFieldRequiredMap,
        unknownSexEnabled
      ) as AnimalSettingsResultV16;

      if (_notNil(newSettings.settings)) {
        setSettings(newSettings.settings);
      }
      setMetadata(_isNil(newSettings.metadata) ? availableMetadata : newSettings.metadata);
      setData(initialBulkAnimalDataV16(selectedAnimals, species) as Array<BulkAnimalRow>);
    }
  }, [
    initialLoad,
    selectedAnimals,
    species,
    availableMetadata,
    metaColumns,
    settings,
    entityFieldRequiredMap,
    unknownSexEnabled,
  ]);

  const backRoute = useMemo(
    () => webRoute(_notNil(studyId) ? 'studies.animals' : 'colony', { id: studyId }),
    [studyId]
  );

  const mapAnimalPatchData = (): Array<AnimalPatchData> => {
    const resolveStrainId = createStrainResolver(species);
    return data.reduce<Array<AnimalPatchData>>((acc, { id, name, sex, dob, species: animalSpecies, strain, meta }) => {
      const animal = selectedAnimals.find((a) => a.id === id);
      if (_isNil(animal?.api_id)) {
        return acc;
      }
      const returnData: AnimalPatchData = {
        api_id: animal.api_id,
        cage_id: _notNil(animal?.cage_id) ? toNumericId(animal.cage_id) : null,
        name,
        dob: dob === '' ? null : dob,
        strain_id: resolveStrainId(animalSpecies, strain),
      };

      if (_notNil(sex)) {
        returnData.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
      }

      if (_isNotEmpty(meta) && _notNil(settings) && _notNil(settings.columns)) {
        const metaArray = createMetaV16(meta, settings.columns);
        returnData.meta = metaArray.reduce<Record<string, MetadataFieldValueTypes>>((acc, item) => {
          acc[String(item.glossary_id)] = item.value;
          return acc;
        }, {});
      }

      acc.push(returnData);
      return acc;
    }, []);
  };

  const saveAnimals = async (reasonForChange?: string) => {
    setLoading(true);
    const animals = mapAnimalPatchData();
    try {
      const response = await ApiService.call({
        endpoint: 'PATCH /api/v1/animals',
        body: {
          animals,
          ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
        },
        options: { onError: { throw: true, toast: false, slug: 'bulk-animals-update-many' } },
      });
      if (response.type === 'success') {
        navigate(backRoute);
        successToast(`Successfully added ${animals.length > 1 ? 'animals' : 'animal'}`);
      }
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const saveAction = async () => {
    if (reasonForChangeEnabled) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => saveAnimals(reasonForChange),
      });
      return;
    }
    await saveAnimals();
  };

  const spreadSheet = (
    <>
      <div className="ow-spreadsheet-styles">
        <BulkAnimalsTableV16
          data={data as unknown as Array<Record<string, unknown>>}
          settings={settings as SpreadsheetV16Settings}
          innerRef={tableRef as React.RefObject<HotTableRef>}
          metadata={metadata}
          setMetadata={setMetadata}
          setInvalid={setIsInvalid}
          maxRows={data.length}
        />
      </div>
      <div className="pa4">
        <Button
          onClick={saveAction}
          disabled={loading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
          testId="bulk-animals__submit"
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={() => navigate(backRoute)}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading || initialLoad || _isNil(settings) ? (
        <div style={{ height: 650 }}>
          <Loading txt={`${loading ? 'Updating animal...' : 'Loading...'}`} />
        </div>
      ) : (
        <>
          <SubHeader
            linkToText={_notNil(studyId) ? 'Animals' : 'Colony'}
            link={backRoute}
            testId="bulk-animals__return"
          />
          <div className="ph4">
            <Header mainHeaderText="Edit Animals" />
          </div>
          {_notNil(settings) && _isNotEmpty(data) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any animals"
                text={'Please select animals from inside Colony to proceed'}
                NoDataComponent={
                  <Link to={backRoute} className="link blue">
                    {`Go to ${_notNil(studyId) ? 'Animals' : 'Colony'}`}
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkAnimalEdit;
